import { useWindowSize } from "@commonsku/styles";
import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { SkubotSpinner } from "@commonsku/styles";

type Display = 'desktop' | 'mobile';

const DISPLAY_STYLES = {
    desktop: {
        initialWidth: 1920,
        initialHeight: 1080,
    },
    mobile: {
        initialWidth: 393,
        initialHeight: 852,
    },
};

const IframeContainer = styled.div`
    &&& {
        overflow: hidden;
        display: flex;
        justify-content: center;
        height: 100%;
        width: 100%;
        position: relative;
    }
`;

const LoadingOverlay = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10;
`;

const StyledIframe = styled.iframe<{
    $width: number;
    $height: number;
    $scale: number;
    $display: Display;
    $inset: number;
}>`
    border: none;
    flex: 0 0 auto;
    width: ${({ $width }) => $width}px;
    height: ${({ $height }) => $height}px;
    transform: ${({ $scale }) => `scale(${$scale})`};
    transform-origin: center top;
    border-radius: ${({ $display }) => $display === 'mobile' ? 5 : 0}px;
    margin-top: ${({ $inset }) => $inset}px;
    margin-left: ${({ $inset }) => $inset}px;
`;

interface ResponsivePreviewProps extends React.IframeHTMLAttributes<HTMLIFrameElement> {
    display?: Display;
    containerStyle?: React.CSSProperties;
    inset?: number;
    isLoading?: boolean;
    onLoad?: () => void;
}

export const ResponsivePreview = ({
    display = 'desktop',
    title,
    style,
    containerStyle,
    inset = 0,
    isLoading = false,
    onLoad,
    ...props
}: ResponsivePreviewProps) => {
    const {initialWidth, initialHeight } = useMemo(() => DISPLAY_STYLES[display], [display]);
    const aspectRatio = initialWidth / initialHeight;

    const containerRef = useRef<HTMLDivElement>(null);
    const [iframeSize, setIframeSize] = useState({ width: initialWidth, height: initialHeight, scale: 1 });
    const [windowWidth, windowHeight] = useWindowSize();

    useEffect(() => {
        const handleResize = () => {
            if (containerRef.current) {
                const rect = containerRef.current.getBoundingClientRect();
                // Calculate the max size that fits in the container while maintaining aspect ratio
                let width = rect.width;
                let height = width / aspectRatio;
                if (height > rect.height) {
                    width = rect.height * aspectRatio;
                }
                // Scale so that the iframe fits the container
                const scale = width / initialWidth;
                setIframeSize({ width: initialWidth - inset, height: initialHeight - inset, scale });
            }
        };

        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [aspectRatio, initialWidth, initialHeight, windowWidth, windowHeight, inset]);

    return (
        <IframeContainer ref={containerRef} style={containerStyle}>
            {isLoading && (
                <LoadingOverlay>
                    <SkubotSpinner size="large" />
                </LoadingOverlay>
            )}
            <StyledIframe
                {...props}
                $width={iframeSize.width}
                $height={iframeSize.height}
                $scale={iframeSize.scale}
                $display={display}
                $inset={inset}
                style={style}
                width={initialWidth}
                height={initialHeight}
                title={title}
                onLoad={onLoad}
            />
        </IframeContainer>
    );
};
